import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";
import { ComedianAdmin } from "../../models/comedian-weekly-digest";
import { fetchVerifiedComedians } from "./fetch-verified-comedians";

const PAGE_SIZE = 1000;
const ID_BATCH_SIZE = 100;

/**
 * Fetches all verified, published comedians with their admin user emails.
 * Returns one entry per comedian with all associated admin emails.
 *
 * Uses the same email resolution pattern as getManagerEmails():
 * comedian_dashboard_admins → users_extra for email lookup.
 */
export async function fetchComedianAdmins(
  supabase: SupabaseClient<Database>
): Promise<ComedianAdmin[]> {
  const verified = await fetchVerifiedComedians(supabase);
  if (verified.size === 0) return [];

  const comedianMap = new Map<
    string,
    { comedianName: string; comedianSlug: string }
  >();
  for (const [id, { name, slug }] of verified) {
    comedianMap.set(id, { comedianName: name, comedianSlug: slug });
  }

  const comedianAdminIds = new Map<string, Set<string>>();
  let offset = 0;
  while (true) {
    const { data, error } = await supabase
      .from("comedian_dashboard_admins")
      .select("comedian_id, admin_id")
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-weekly-digest] Failed to fetch comedian_dashboard_admins",
      });
      throw error;
    }

    if (!data || data.length === 0) break;

    for (const row of data) {
      if (!comedianMap.has(row.comedian_id)) continue;

      let adminSet = comedianAdminIds.get(row.comedian_id);
      if (!adminSet) {
        adminSet = new Set();
        comedianAdminIds.set(row.comedian_id, adminSet);
      }
      adminSet.add(row.admin_id);
    }

    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  const allAdminIds = [
    ...new Set(
      Array.from(comedianAdminIds.values()).flatMap((s) => Array.from(s))
    ),
  ];

  const emailMap = new Map<string, string>();
  for (let i = 0; i < allAdminIds.length; i += ID_BATCH_SIZE) {
    const chunk = allAdminIds.slice(i, i + ID_BATCH_SIZE);
    const { data: users, error: usersError } = await supabase
      .from("users_extra")
      .select("id, email")
      .in("id", chunk)
      .not("email", "is", null);

    if (usersError) {
      logException(usersError, {
        data: "[comedian-weekly-digest] Failed to fetch admin emails",
      });
      throw usersError;
    }

    for (const user of users ?? []) {
      if (user.email) {
        emailMap.set(user.id, user.email);
      }
    }
  }

  const results: ComedianAdmin[] = [];
  for (const [comedianId, adminIds] of comedianAdminIds) {
    const comedian = comedianMap.get(comedianId);
    if (!comedian) continue;

    const adminEmails: Array<{ userId: string; email: string }> = [];
    for (const adminId of adminIds) {
      const email = emailMap.get(adminId);
      if (email) {
        adminEmails.push({ userId: adminId, email });
      }
    }

    if (adminEmails.length > 0) {
      results.push({
        comedianId,
        comedianName: comedian.comedianName,
        comedianSlug: comedian.comedianSlug,
        adminEmails,
      });
    }
  }

  return results;
}
